import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import StatusBadge from '@/components/ui/StatusBadge';
import Toast from '@/components/ui/Toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePerms } from '@/lib/perms';
import { onboardingApi, type ListingRow, type ListingTypes, type ListingsPayload, type PartnerFull } from '@/lib/onboarding';
import CabinetStep from './components/CabinetStep';
import PriceListStep from './components/PriceListStep';
import PreviewStep from './components/PreviewStep';
import ApprovalStep from './components/ApprovalStep';
import ContractStep from './components/ContractStep';
import SendStep from './components/SendStep';

const STEPS = ['cabinet', 'pricelist', 'preview', 'approval', 'contract', 'send'] as const;
type Step = (typeof STEPS)[number];

/** Онбординг поставщика на выезде (этап 1.2б): шесть шагов на реальных данных; текущий шаг — в ?step=. */
export default function OnboardingPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const step = (params.get('step') as Step) || 'cabinet';
  const [partner, setPartner] = useState<PartnerFull | null>(null);
  const [data, setData] = useState<ListingsPayload | null>(null);
  const [types, setTypes] = useState<ListingTypes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone?: 'success' | 'error' } | null>(null);
  const { can, action } = usePerms();
  const canEdit = can('partners', 'write');
  const isAdmin = action('send_to_site');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [p, l] = await Promise.all([onboardingApi.partner(id), onboardingApi.listings(id)]);
      setPartner(p);
      setData(l);
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [id]);
  useEffect(() => {
    void load();
    onboardingApi.types().then(setTypes).catch(() => undefined);
  }, [load]);

  const setStep = (s: Step) => {
    const q = new URLSearchParams(params);
    q.set('step', s);
    setParams(q, { replace: true });
  };
  const onToast = (message: string, tone: 'success' | 'error' = 'success') => setToast({ message, tone });
  const onRow = (row: ListingRow) => setData((d) => (d ? { ...d, rows: d.rows.map((r) => (r.listing.sku === row.listing.sku ? row : r)) } : d));
  const onPartner = (p: PartnerFull) => {
    setPartner(p);
    void load();
  };

  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <p className="text-sm font-medium text-foreground-900">{t('onb.error', { code: error })}</p>
        <Link to="/partners" className="mt-2 inline-block text-[12px] font-semibold text-primary-700">{t('pd.backToPartners')}</Link>
      </div>
    );
  }
  if (!partner || !data) return <div className="p-8 text-center text-[12.5px] text-foreground-500">…</div>;

  const live = data.rows.filter((r) => r.listedOnSite).length;
  const done: Record<Step, boolean> = {
    cabinet: Boolean(partner.trn && partner.warehouse),
    pricelist: data.rows.length > 0,
    preview: data.rows.some((r) => r.images.length > 0),
    approval: Boolean(data.summary.approval),
    contract: data.summary.contractOk,
    send: live > 0,
  };
  const statusTone = partner.status === 'Active' ? 'primary' : partner.status === 'Paused' ? 'danger' : 'accent';

  return (
    <div>
      <Link to={`/partners/${partner.id}`} className="mb-3 inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line text-[12px]" /> {t('onb.back')}
      </Link>
      <PageHeader
        eyebrow={t('onb.eyebrow')}
        title={t('onb.title', { name: partner.name })}
        description={t('onb.description')}
        actions={
          <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
            <StatusBadge tone={statusTone as 'primary' | 'danger' | 'accent'}>{t(`status.${partner.status}`)}</StatusBadge>
            <span className="text-foreground-500">{t('onb.summaryContract')}: <b className={data.summary.contractOk ? 'text-primary-700' : 'text-rose-600'}>{data.summary.contractOk ? t('onb.yes') : t('onb.no')}</b></span>
            <span className="text-foreground-500">{t('onb.summaryApproval')}: <b className={data.summary.approval ? 'text-primary-700' : 'text-rose-600'}>{data.summary.approval ? t('onb.yes') : t('onb.no')}</b></span>
            <span className="text-foreground-500">{t('onb.summaryReady')}: <b className="tnum text-foreground-900">{data.summary.ready}</b></span>
            <span className="text-foreground-500">{t('onb.summaryLive')}: <b className="tnum text-foreground-900">{live}</b></span>
          </div>
        }
      />
      <div className="mb-5 flex flex-wrap gap-1 border-b border-background-200">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(s)} className={`relative -mb-px flex cursor-pointer items-center gap-2 px-3 py-2.5 text-[12.5px] font-medium ${step === s ? 'text-primary-700' : 'text-foreground-500 hover:text-foreground-900'}`}>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10.5px] font-semibold ${done[s] ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'}`}>{done[s] ? <i className="ri-check-line" /> : i + 1}</span>
            {t(`onb.steps.${s}`)}
            {step === s && <span className="absolute inset-x-1 -bottom-px h-[2px] rounded-full bg-primary-500" />}
          </button>
        ))}
      </div>

      {step === 'cabinet' && <CabinetStep partner={partner} onSaved={onPartner} onToast={onToast} />}
      {step === 'pricelist' && <PriceListStep partner={partner} />}
      {step === 'preview' && <PreviewStep data={data} types={types} onRow={onRow} onReload={() => void load()} onToast={onToast} canEdit={Boolean(canEdit)} />}
      {step === 'approval' && <ApprovalStep data={data} onReload={() => void load()} onToast={onToast} canEdit={Boolean(canEdit)} />}
      {step === 'contract' && <ContractStep partner={partner} onPartner={onPartner} onToast={onToast} canEdit={Boolean(canEdit)} />}
      {step === 'send' && <SendStep data={data} isAdmin={Boolean(isAdmin)} onReload={() => void load()} onRow={onRow} onToast={onToast} />}

      {toast && <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} />}
    </div>
  );
}
